/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { useEuiTheme } from '../../services';
import { euiLoadingChartStyles, euiLoadingChartBarStyles, _barIndex } from './loading_chart.styles';
import { useEuiI18n } from '../i18n';
export var SIZES = ['m', 'l', 'xl'];
export var EuiLoadingChart = ({
  size = 'm',
  mono = false,
  className,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const defaultAriaLabel = useEuiI18n('euiLoadingChart.ariaLabel', 'Loading');
  const euiTheme = useEuiTheme();
  const styles = euiLoadingChartStyles(euiTheme);
  const barStyles = euiLoadingChartBarStyles(euiTheme);
  const classes = classNames('euiLoadingChart', {
    'euiLoadingChart--mono': mono
  }, className);
  const cssStyles = [styles.euiLoadingChart, styles[size]];
  const cssBarStyles = index => {
    return [barStyles.euiLoadingChart__bar, barStyles[size], _barIndex(index, mono, euiTheme)];
  };
  const bars = [];
  for (let index = 0; index < 4; index++) {
    bars.push(<span key={index} css={cssBarStyles(index)} />);
  }
  return <span className={classes} css={cssStyles} role="progressbar" aria-label={ariaLabel || defaultAriaLabel} {...rest}>
      {bars}
    </span>;
};
EuiLoadingChart.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  css: PropTypes.any,
  size: PropTypes.any,
  mono: PropTypes.bool
};
try {
  EuiLoadingChart.__docgenInfo = {
    tags: {},
    filePath: '/app/src/components/loading/loading_chart.tsx',
    description: '',
    displayName: 'EuiLoadingChart',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      css: {
        defaultValue: null,
        description: '',
        name: 'css',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'Interpolation<Theme>'
        }
      },
      size: {
        defaultValue: {
          value: 'm'
        },
        description: '',
        name: 'size',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/loading/loading_chart.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"m" | "l" | "xl"',
          value: [{
            value: '"m"'
          }, {
            value: '"l"'
          }, {
            value: '"xl"'
          }]
        }
      },
      mono: {
        defaultValue: {
          value: 'false'
        },
        description: '',
        name: 'mono',
        parent: undefined,
        declarations: [{
          fileName: 'app/src/components/loading/loading_chart.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'boolean'
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}